import type { OrgRow } from '~/gql/organizations/organizations'
import { useMutation } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { GQL_DELETE_ORGANIZATION } from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void | Promise<unknown>
  org: OrgRow
}

/** Traduit les codes d'erreur métier du serveur en message lisible. */
function deleteErrorKey(message: string): string {
  if (message.includes('ORG_NOT_EMPTY')) return 'organization.delete.notEmpty'
  if (message.includes('CANNOT_DELETE_DEFAULT_ORG')) return 'organization.delete.default'
  return 'organization.delete.failed'
}

function OrganizationDeleteModal({ open, onCancel, onSuccess, org }: IProps) {
  const { t } = useTranslation()
  const [deleteOrganization, { loading }] = useMutation(GQL_DELETE_ORGANIZATION, { client })

  const onConfirm = async () => {
    try {
      await deleteOrganization({ variables: { id: org.id } })
      toast.success(t('organization.delete.success'))
      await onSuccess()
      onCancel()
    } catch (err) {
      toast.error(t(deleteErrorKey(err instanceof Error ? err.message : '')))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('organization.delete.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 text-sm text-muted-foreground">
          <p>{t('organization.delete.confirm', { name: org.name })}</p>
          {org.userCount > 0 && (
            <p className="text-destructive">
              {t('organization.delete.notEmpty')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading || org.userCount > 0}>
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default OrganizationDeleteModal
